import styled from "@emotion/styled"
import { PButtonPure, PHeading, PText } from "@porsche-design-system/components-react"
import {
	colorContrastLow,
	leadingNormal,
	spacingStaticMd,
	spacingStaticSm,
} from "@porsche-design-system/components-react/emotion"
import type { PriceBreakdownView } from "@slfinrtl/ui-bff-routes"
import { useCallback, useState } from "react"
import { FormattedMessage } from "react-intl"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { Skeleton } from "../../../../../skeleton"
import { priceBreakdownMessages } from "./priceBreakdown.messages"

interface PriceBreakdownProps {
	collapsible: boolean
	skeletonPrices?: boolean
	view: PriceBreakdownView
}

export const PriceBreakdown = ({ collapsible, skeletonPrices = false, view }: PriceBreakdownProps) => {
	const [isCollapsibleOpen, setIsCollapsibleOpen] = useState(!collapsible)

	const toggleIsCollapsibleOpen = useCallback(() => setIsCollapsibleOpen((prev) => !prev), [setIsCollapsibleOpen])

	return (
		<>
			{isCollapsibleOpen && (
				<CategoryList>
					{view.categories.map((category) => {
						if (category.items.length === 0) {
							return (
								<CategoryContainer key={category.name}>
									<SpaceBetween>
										<PHeading size="sm">{category.name}</PHeading>
										{skeletonPrices ? <PriceSkeleton /> : <PText>{category.totalPrice}</PText>}
									</SpaceBetween>
								</CategoryContainer>
							)
						}
						return (
							<CategoryContainer key={category.name}>
								<CategoryHeading size="sm">{category.name}</CategoryHeading>
								{category.items.map((item, itemIndex) => (
									<SpaceBetween key={itemIndex}>
										<PText>{item.name}</PText>
										{skeletonPrices ? <PriceSkeleton /> : <PText>{item.price}</PText>}
									</SpaceBetween>
								))}
								<SpaceBetweenTotal>
									<PHeading size="sm">
										<FormattedMessage
											{...priceBreakdownMessages.categoryTotalPrice}
											values={{ categoryName: category.name }}
										/>
									</PHeading>
									{skeletonPrices ? <PriceSkeleton /> : <PText>{category.totalPrice}</PText>}
								</SpaceBetweenTotal>
							</CategoryContainer>
						)
					})}
				</CategoryList>
			)}
			<HeadlinePFlex $collapsible={collapsible} $showPricing={isCollapsibleOpen}>
				<PHeading size="sm">
					<FormattedMessage {...priceBreakdownMessages.totalPrice} />
				</PHeading>
				{skeletonPrices ? <PriceSkeleton /> : <PText>{view.totalPrice}</PText>}
			</HeadlinePFlex>
			{collapsible && (
				<StyledButtonPure icon="none" underline onClick={toggleIsCollapsibleOpen}>
					<FormattedMessage
						{...(isCollapsibleOpen
							? priceBreakdownMessages.hidePricing
							: priceBreakdownMessages.showPricing)}
					/>
				</StyledButtonPure>
			)}
		</>
	)
}

const HeadlinePFlex = styled("div", { shouldForwardProp })<{
	$collapsible: boolean
	$showPricing: boolean
}>`
	display: flex;
	justify-content: space-between;
	padding-bottom: ${spacingStaticMd};
	${(props) => (props.$showPricing ? `padding-top: ${spacingStaticMd};` : "")}
	${(props) => (props.$collapsible ? `border-bottom: 1px solid ${colorContrastLow};` : "")}
`

const CategoryList = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticMd};
	margin-top: ${spacingStaticMd};
`

const CategoryContainer = styled.div`
	padding: 0 0 ${spacingStaticMd};
	border-bottom: 1px solid ${colorContrastLow};
`

const CategoryHeading = styled(PHeading)`
	margin-bottom: ${spacingStaticSm};
`

const StyledButtonPure = styled(PButtonPure)`
	margin-top: ${spacingStaticSm};
`

const PriceSkeleton = styled(Skeleton.Text)`
	height: calc(${leadingNormal} - 2px);
	width: 60px;
`

const SpaceBetween = styled.div`
	display: flex;
	justify-content: space-between;
`

const SpaceBetweenTotal = styled(SpaceBetween)`
	margin-top: ${spacingStaticSm};
`
